/**
 * Image utility functions for customer web portal
 */

import { apiConfig } from '../config/api';

export interface ImageValidationResult {
  isValid: boolean;
  processedUrl: string;
  error?: string;
}

// Local fallback image as data URI (works offline, no external dependency)
const FALLBACK_IMAGE_DATA_URI = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzY2N2VlYSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiNmZmZmZmYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';

// Helper function to get fallback image (can be customized per size if needed)
const getFallbackImage = (_width: number = 300, _height: number = 200): string => {
  // Use data URI for offline support
  return FALLBACK_IMAGE_DATA_URI;
};

/**
 * Validates and processes image URLs for the customer web portal
 */
export const getValidImageUrl = (imageUrl: string): string => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    console.log('🌐 ImageUtils - No image URL provided, using fallback');
    return getFallbackImage();
  }

  // Trim whitespace
  const trimmedUrl = imageUrl.trim();

  // Check if it's a valid HTTP URL
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    // Additional validation for common issues
    if (trimmedUrl.includes('3008') && !trimmedUrl.includes('4555')) {
      console.log('🌐 ImageUtils - Detected old port 3008 URL, using fallback:', trimmedUrl);
      return getFallbackImage();
    }
    
    // Handle upload URLs - they may be on port 3000 (API gateway) or port 3003 (shop service)
    // Example: http://localhost:3000/api/v1/uploads/shop-logos/9ebe5862-aeb9-4748-ac06-1a18cd3cb1fa_20251218_151859.png
    // Keep the original URL as-is since uploads are served from the API gateway (port 3000)
    // Log for debugging purposes
    if (trimmedUrl.includes('/uploads/')) {
      console.log('🌐 ImageUtils - Detected upload URL (keeping as-is):', trimmedUrl);
      // Return as-is - upload URLs are correct as stored in the database
      // They point to the API gateway which serves static uploads
      return trimmedUrl;
    }
    
    return trimmedUrl;
  }

  // Check if it's a relative URL (starts with / or ./)
  if (trimmedUrl.startsWith('/') || trimmedUrl.startsWith('./')) {
    // For relative URLs, we need to construct an absolute URL
    // Use shop service URL as base (port 3003) or fallback to base URL
    const baseUrl = (apiConfig as any).shopServiceUrl || apiConfig.baseUrl || 'http://localhost:3003';
    // Remove /api/v1 or /api if present to get the base server URL
    const base = baseUrl.replace(/\/api\/v1$/, '').replace(/\/api$/, '');
    const absoluteUrl = `${base}${trimmedUrl.startsWith('./') ? trimmedUrl.substring(1) : trimmedUrl}`;
    console.log('🌐 ImageUtils - Relative URL detected, converting to absolute:', trimmedUrl, '->', absoluteUrl);
    return absoluteUrl;
  }

  // Check if URL is missing protocol but looks like a valid domain
  // Pattern: starts with a domain name (letters, numbers, dots, hyphens) or www.
  const domainPattern = /^(www\.|[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}/;
  if (domainPattern.test(trimmedUrl)) {
    console.log('🌐 ImageUtils - URL missing protocol, adding https://:', trimmedUrl);
    return `https://${trimmedUrl}`;
  }

  // Check if it's a partial URL (like the 3008 port issue)
  if (trimmedUrl.includes('3008') || trimmedUrl.includes('localhost:3008')) {
    console.log('🌐 ImageUtils - Detected old port 3008 URL, using fallback:', trimmedUrl);
    return getFallbackImage();
  }

  // Check for emoji or invalid content
  const emojiPatterns = ['🛍️', '📦', '🌾', '🥛', '🍞', '🍩', '🍫', '🥨', '🥟', '🍯'];
  if (emojiPatterns.some(emoji => trimmedUrl.includes(emoji))) {
    console.log('🌐 ImageUtils - Detected emoji in image URL, using fallback:', trimmedUrl);
    return getFallbackImage();
  }

  // Check for placeholder URLs that are missing protocol
  if (trimmedUrl.includes('via.placeholder.com')) {
    if (trimmedUrl.startsWith('via.placeholder.com')) {
      console.log('🌐 ImageUtils - Placeholder URL missing protocol, adding https://:', trimmedUrl);
      return `https://${trimmedUrl}`;
    }
    // If it already has protocol but is a placeholder, return as is
    return trimmedUrl;
  }

  // Default fallback
  console.log('🌐 ImageUtils - Invalid image URL, using fallback:', trimmedUrl);
  return getFallbackImage();
};

/**
 * Validates image URL and returns detailed result
 */
export const validateImageUrl = (imageUrl: string): ImageValidationResult => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return {
      isValid: false,
      processedUrl: getFallbackImage(),
      error: 'No image URL provided'
    };
  }

  // Check if it's a valid HTTP URL
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    // Check for old port issues
    if (imageUrl.includes('3008') && !imageUrl.includes('4555')) {
      return {
        isValid: false,
        processedUrl: getFallbackImage(),
        error: 'Old port 3008 detected, should use port 4555'
      };
    }
    return {
      isValid: true,
      processedUrl: imageUrl
    };
  }

  // Check for emoji patterns
  const emojiPatterns = ['🛍️', '📦', '🌾', '🥛', '🍞', '🍩', '🍫', '🥨', '🥟', '🍯'];
  if (emojiPatterns.some(emoji => imageUrl.includes(emoji))) {
    return {
      isValid: false,
      processedUrl: getFallbackImage(),
      error: 'Emoji characters detected in image URL'
    };
  }

  return {
    isValid: false,
    processedUrl: getFallbackImage(),
    error: 'Invalid image URL format'
  };
};

/**
 * Processes product images for display
 */
export const processProductImages = (product: any): {
  primaryImage: string;
  allImages: string[];
  hasValidImages: boolean;
} => {
  const defaultImage = getFallbackImage();
  
  if (!product || !product.image) {
    return {
      primaryImage: defaultImage,
      allImages: [defaultImage],
      hasValidImages: false
    };
  }

  const processedUrl = getValidImageUrl(product.image);
  const hasValidImages = processedUrl !== defaultImage;

  return {
    primaryImage: processedUrl,
    allImages: [processedUrl],
    hasValidImages
  };
};





